import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { updateNPSByUsername } from '@/api/user';
import { APP_LANDING_PAGE, AppRoutes, BOOK_CONSULTATION_LANDING_PAGE } from 'src/enums/ERoutes';
import { useFavicon } from '@/routes/hooks/useFavicon';
import logo from '@/assets/images/new_logo.png';

interface AcknowledgementContent {
	bigText: string;
	mediumText: string;
	infoText: string;
}

const getAcknowledgementContent = (id?: string, nps?: string): AcknowledgementContent => {
	if (id && nps) {
		return {
			bigText: 'THANK YOU',
			mediumText: 'Your feedback has been recorded',
			infoText: 'We appreciate your time and will use this to improve our service.',
		};
	}

	return {
		bigText: 'THANK YOU',
		mediumText: 'For Reaching Out',
		infoText: 'We appreciate your time and will get back to you soon.',
	};
};

export const Acknowledgement: React.FC = () => {
	const [searchParams] = useSearchParams();
	const id = searchParams.get('id');
	const nps = searchParams.get('score');

	useFavicon({
		isUnbrandedPath: false,
	});

	const updateNPSMutation = useMutation({
		mutationFn: async (score: number) => {
			return updateNPSByUsername({ username: id, nps: score });
		},
	});

	useEffect(() => {
		if (id && nps && !updateNPSMutation.isPending && !updateNPSMutation.isSuccess) {
			const score = parseInt(nps, 10);
			if (!isNaN(score) && score >= 0 && score <= 10) {
				updateNPSMutation.mutate(score);
			}
		}
	}, [id, nps]);

	const content = getAcknowledgementContent(id, nps);

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
			<div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-10">
				<a
					href="https://app.myaifrontdesk.com"
					target="_blank"
					rel="noopener noreferrer"
					className="transition-transform duration-200 hover:scale-110"
				>
					<img
						src={logo}
						alt="AI Frontdesk Logo"
						className="w-16 h-16 rounded-lg border-2 border-gray-200 bg-white p-1 shadow-sm"
					/>
				</a>
			</div>

			<div className="text-center">
				<h1 className="text-8xl md:text-9xl font-bold text-blue-500 mb-4 transition-all duration-500 hover:scale-105 hover:text-blue-600 cursor-default select-none">
					{content.bigText}
				</h1>

				<h2 className="text-xl md:text-2xl text-blue-600 font-medium mb-6 transition-colors duration-300 hover:text-blue-700">
					{content.mediumText}
				</h2>

				<p className="text-gray-600 text-base md:text-lg mb-12 max-w-md mx-auto transition-colors duration-300 hover:text-gray-700">
					{content.infoText}
				</p>

				<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
					<Link
						to={AppRoutes.DASHBOARD}
						className="group flex cursor-pointer items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 transform active:scale-95"
					>
						<svg
							className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
							/>
						</svg>
						<span className="transition-all duration-300 group-hover:tracking-wide">Go Home</span>
					</Link>

					<Link
						to={AppRoutes.RECEPTIONIST_LOGIN}
						className="group flex cursor-pointer items-center gap-2 bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-all duration-300 hover:shadow-md hover:scale-105 transform active:scale-95"
					>
						<span className="transition-all duration-300 group-hover:tracking-wide">Login</span>
						<svg
							className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:scale-110"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M17 8l4 4m0 0l-4 4m4-4H3"
							/>
						</svg>
					</Link>
				</div>
			</div>

			<div className="w-4/5 mt-16">
				<div className="h-1 bg-gray-200 rounded-full overflow-hidden">
					<div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 w-1/3 transition-all duration-1000 ease-out hover:w-2/3 rounded-full" />
				</div>

				<div className="bg-white py-4 px-6 flex justify-between items-center text-sm text-gray-500 rounded-b-lg shadow-sm">
					<Link
						target="_blank"
						to={APP_LANDING_PAGE}
						className="group relative transition-all duration-300 hover:text-blue-600 hover:font-medium"
					>
						<span className="relative z-10">Our Products</span>
						<div className="absolute inset-0 bg-blue-50 rounded-md scale-0 group-hover:scale-100 transition-transform duration-300 origin-center" />
						<div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300" />
					</Link>

					<Link
						target="_blank"
						to={BOOK_CONSULTATION_LANDING_PAGE}
						className="group relative transition-all duration-300 hover:text-blue-600 hover:font-medium"
					>
						<span className="relative z-10">Need a consultation?</span>
						<div className="absolute inset-0 bg-blue-50 rounded-md scale-0 group-hover:scale-100 transition-transform duration-300 origin-center" />
						<div className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300" />
					</Link>
				</div>
			</div>
		</div>
	);
};

export default Acknowledgement;
